import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { API_URL } from '../../../../config';

interface ClipData {
  url: string;
  metadata: Record<string, unknown> | null;
  storageKey: string;
}

export function useWorkflowRunClip(workflowId: string, runId: string, hasVideo: boolean) {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const clipQuery = useQuery({
    queryKey: ['workflow-run-clip', workflowId, runId],
    queryFn: async (): Promise<ClipData | null> => {
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/runs/${runId}/clip`,
        { credentials: 'include' },
      );
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Failed to fetch clip');
      const json = await res.json();
      return json.data as ClipData;
    },
    enabled: hasVideo,
    refetchInterval: isGenerating ? 5000 : false,
  });

  // Stop polling once clip arrives
  if (isGenerating && clipQuery.data) {
    setIsGenerating(false);
  }

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/runs/${runId}/generate-clip`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(json.error ?? 'Failed to generate clip');
      }
      return res.json();
    },
    onSuccess: () => {
      setIsGenerating(true);
      queryClient.invalidateQueries({ queryKey: ['workflow-run-clip', workflowId, runId] });
    },
  });

  return {
    clip: clipQuery.data ?? null,
    isLoading: clipQuery.isLoading,
    isGenerating,
    generate: generateMutation.mutate,
    generateError: generateMutation.error,
  };
}
